import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import COUNTRIES from "../../data/countries.json";
import { DIAL_CODES, flagEmoji } from "../billing/dialCodes";
import { PHONE_LEN } from "../billing/phoneLengths";
import { parseEmergency } from "./idValidate";

const SEL = "neu-select h-12 w-full rounded-2xl bg-background px-4 text-sm shadow-neu-in outline-none focus:ring-2 focus:ring-ring";
const BOX = "flex items-stretch overflow-hidden rounded-2xl bg-background shadow-neu-in transition-shadow";

// هاتف الطوارئ: اختيار كود الدولة + رقم بطول ثابت حسب الدولة (لا يقبل زيادة).
export const EmergencyPhone = ({ label, value, onChange }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const options = useMemo(
    () => COUNTRIES.filter((c) => DIAL_CODES[c.code])
      .map((c) => ({ ...c, dial: DIAL_CODES[c.code], flag: flagEmoji(c.code) }))
      .sort((a, b) => (isAr ? a.ar.localeCompare(b.ar, "ar") : a.en.localeCompare(b.en))),
    [isAr],
  );
  const [code, setCode] = useState("");
  const [local, setLocal] = useState("");
  useEffect(() => {
    const p = parseEmergency(value);
    setCode(p.code);
    setLocal(p.local);
  }, [value]);

  const current = options.find((c) => c.code === code) || null;
  const expected = PHONE_LEN[code] || 9;
  const isValid = !!code && local.length === expected;
  const showRed = !!code && local.length > 0 && !isValid;
  const border = !code ? "" : isValid ? "ring-2 ring-emerald-500/40" : showRed ? "ring-2 ring-destructive/40" : "";
  const emit = (c, l) => onChange(c && l ? `${DIAL_CODES[c]}${l}` : "");
  const chooseCountry = (c) => { setCode(c); setLocal(""); emit(c, ""); };
  const onLocal = (e) => {
    let v = e.target.value.replace(/[^\d]/g, "").replace(/^0+/, "");
    if (v.length > expected) v = v.slice(0, expected);
    setLocal(v);
    emit(code, v);
  };
  const tone = isValid ? "text-emerald-600" : showRed ? "text-destructive" : "text-muted-foreground";

  return (
    <>
      <div>
        <p className="label-xs mb-2">{t("signup.country")}</p>
        <select data-testid="emergency-country" value={code} onChange={(e) => chooseCountry(e.target.value)} className={SEL}>
          <option value="">{t("signup.selectCountry")}</option>
          {options.map((c) => (
            <option key={c.code} value={c.code}>{c.flag} {isAr ? c.ar : c.en} ({c.dial})</option>
          ))}
        </select>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="label-xs">{label}</p>
          {code ? <span data-testid="emergency-phone-counter" className={`num text-xs font-bold ${tone}`}>{local.length}/{expected}</span> : null}
        </div>
        <div dir="ltr" className={`${BOX} ${border}`}>
          <span data-testid="emergency-phone-code" className="num flex select-none items-center gap-1.5 border-e border-border/60 bg-muted/40 px-3 font-mono text-sm font-bold text-primary">
            <span className="text-base">{current?.flag || "🌍"}</span>
            <span>{current?.dial || "+—"}</span>
          </span>
          <input type="tel" dir="ltr" data-testid="emergency-phone-local" disabled={!code} value={local} onChange={onLocal}
                 placeholder={code ? "X".repeat(expected) : t("signup.selectCountryFirst")} maxLength={expected}
                 className="num flex-1 bg-transparent px-3 py-3 font-mono text-sm outline-none disabled:opacity-60" />
        </div>
        {showRed ? <p data-testid="emergency-phone-error" className="mt-1 text-xs font-semibold text-destructive">{t("signup.phoneLenError", { n: expected })}</p> : null}
      </div>
    </>
  );
};
